"use client";

import { useState } from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/atoms/tooltip";

interface ExpandableTextProps {
  text: string;
  maxLength?: number;
  className?: string;
  showMoreText?: string;
  showLessText?: string;
  tooltipMaxWidth?: string;
  minWidth?: string;
  multiline?: boolean;
  maxLines?: number;
}

export function ExpandableText({
  text,
  maxLength = 30,
  className = "",
  showMoreText = "More",
  showLessText = "Less",
  tooltipMaxWidth = "max-w-xs",
  minWidth = "min-w-0",
  multiline = false,
  maxLines = 2,
}: ExpandableTextProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleExpansion = () => {
    setIsExpanded(!isExpanded);
  };

  const truncateText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.slice(0, maxLength) + "...";
  };

  const isTextLong = text.length > maxLength;

  // For multiline mode, check if text needs truncation based on both length and lines
  const needsTruncation = multiline
    ? text.split("\n").length > maxLines || text.length > maxLength
    : isTextLong;

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation();
    toggleExpansion();
  };

  return (
    <div
      className={`flex ${multiline ? "flex-col items-start" : "items-center"} gap-2 relative z-10 ${minWidth} ${className}`}>
      <div
        className={`flex ${multiline ? "flex-col" : "items-center"} gap-2 w-full`}>
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              className={`cursor-pointer relative z-10 ${
                isExpanded
                  ? "break-words whitespace-pre-wrap"
                  : multiline
                    ? `line-clamp-${maxLines}`
                    : "truncate"
              }`}
              onClick={handleClick}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}>
              {isExpanded
                ? text
                : multiline
                  ? needsTruncation
                    ? truncateText(text, maxLength)
                    : text
                  : truncateText(text, maxLength)}
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <div className={`${tooltipMaxWidth} break-words`}>{text}</div>
          </TooltipContent>
        </Tooltip>
        {needsTruncation && (
          <button
            type="button"
            onClick={handleClick}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            className={`text-blue-600 hover:text-blue-800 text-xs font-medium shrink-0 relative z-10 ${
              multiline ? "self-start" : ""
            }`}>
            {isExpanded ? showLessText : showMoreText}
          </button>
        )}
      </div>
    </div>
  );
}
